import { InputProps, Space, Spin } from 'antd';
import { StyledInfoCircleFilled } from 'App.styles';
import IconTooltip from 'components/ui/base/tooltip/IconTooltip';
import { FieldHelperProps, FieldInputProps, FieldMetaProps } from 'formik';
import { CSSProperties, FC, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ErrorDiv,
    ErrorLabel,
    FormItem,
    InputDiv,
    StyledInput,
    SuccessDiv,
    SuccessIcon,
    SuccessLabel,
    Wrapper,
} from './styles';
import { RefWrapper } from '../formSelect/styles';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';

export interface FormInputProps {
    type?: InputProps['type'];
    label?: string;
    children?: ReactNode;
    fieldProps: FieldInputProps<any>;
    fieldMeta?: FieldMetaProps<any>;
    fieldHelper?: FieldHelperProps<any>;
    disabled?: boolean;
    required?: boolean;
    maxLength?: number;
    autoFocus?: boolean;
    showSuccess?: boolean;
    tooltip?: string;
    refClicked?: (refNumber: string | number) => void;
    refNumber?: string;
    isSearching?: boolean;
    condensed?: boolean;
    placeholder?: string;
    inputStyles?: CSSProperties;
    noPrefixGap?: boolean;
    prefix?: ReactNode;
}

const FormInput: FC<FormInputProps> = ({
    type,
    label,
    fieldMeta,
    fieldProps,
    fieldHelper,
    disabled,
    required,
    maxLength,
    autoFocus,
    showSuccess,
    tooltip,
    refClicked,
    refNumber,
    isSearching,
    condensed,
    placeholder,
    inputStyles,
    prefix,
    noPrefixGap,
}) => {
    const { t } = useTranslation('common');

    useEffect(() => {
        if (fieldProps.value === '' && fieldHelper) {
            fieldHelper.setValue(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldProps.value]);

    const handleRefClick = useCallback(() => {
        if (refClicked) {
            refNumber ? refClicked(refNumber) : refClicked(label ?? '');
        }
    }, [refClicked, refNumber, label]);

    const iconInput = useMemo(() => {
        if (!!isSearching) {
            return <Spin size="small" />;
        }
        return showSuccess ? <SuccessIcon /> : <span />;
    }, [isSearching, showSuccess]);

    if (label) {
        return (
            <Wrapper>
                <Space size={8}>
                    {refNumber && <RefWrapper>{refNumber}</RefWrapper>}
                    {required && <LabelRequiredSymbol />}
                    {label && <span>{label}</span>}
                    {tooltip && (
                        <IconTooltip
                            title={label}
                            tooltip={tooltip ?? t('defaultTooltip')}
                            icon={<StyledInfoCircleFilled />}
                            tooltipClicked={handleRefClick}
                        />
                    )}
                </Space>
                <FormItem style={inputStyles}>
                    <InputDiv>
                        <StyledInput
                            $noPrefixGap={noPrefixGap}
                            prefix={prefix}
                            id={fieldProps.name}
                            type={type}
                            showSuccess={showSuccess ?? false}
                            suffix={iconInput}
                            autoFocus={autoFocus}
                            disabled={disabled}
                            maxLength={maxLength}
                            status={fieldMeta?.error && !!fieldMeta?.touched ? 'error' : ''}
                            {...fieldProps}
                            size={condensed ? 'middle' : 'large'}
                            condensed={condensed}
                            placeholder={placeholder}
                        />
                    </InputDiv>
                    {!showSuccess && fieldMeta?.error && fieldMeta?.touched && (
                        <ErrorDiv error={true} touched={true} condensed={condensed}>
                            <ErrorLabel>{fieldMeta?.error}</ErrorLabel>
                        </ErrorDiv>
                    )}

                    {showSuccess && (
                        <SuccessDiv>
                            <SuccessLabel>The EORI code is valid!</SuccessLabel>
                        </SuccessDiv>
                    )}
                </FormItem>
            </Wrapper>
        );
    }

    return (
        <FormItem style={inputStyles}>
            <InputDiv>
                <StyledInput
                    $noPrefixGap={noPrefixGap}
                    prefix={prefix}
                    id={fieldProps.name}
                    type={type}
                    showSuccess={showSuccess ?? false}
                    suffix={iconInput}
                    autoFocus={autoFocus}
                    disabled={disabled}
                    maxLength={maxLength}
                    status={fieldMeta?.error && !!fieldMeta?.touched ? 'error' : ''}
                    {...fieldProps}
                    size={condensed ? 'middle' : 'large'}
                    condensed={condensed}
                    placeholder={placeholder}
                />
            </InputDiv>
            {!showSuccess && fieldMeta?.error && fieldMeta?.touched && (
                <ErrorDiv error={true} touched={true} condensed={condensed}>
                    <ErrorLabel>{fieldMeta?.error}</ErrorLabel>
                </ErrorDiv>
            )}

            {showSuccess && (
                <SuccessDiv>
                    <SuccessLabel>The EORI code is valid!</SuccessLabel>
                </SuccessDiv>
            )}
        </FormItem>
    );
};

export default FormInput;
